import { useLocation } from 'preact-iso'
import { Send } from 'lucide-preact'
import { routes } from '@/utils/routes'
import { relativeFromNow } from '@/components/webs/email-blasts/engagementFormat'
import type { ArmedBroadcastSummaryDto } from '@/types/emailActivity'

interface Props {
    armed: ArmedBroadcastSummaryDto[]
    clientCompanyId: string
    workspaceId: string
}

// The tinted "Armed broadcasts" band above the queue (Upcoming filter only): broadcasts scheduled but not
// yet materialized into per-recipient rows, with an approximate target-list size. Renders nothing when
// there are none. Each row opens the broadcast's web (where the armed activation is managed).
export function EmailActivityArmedBand({ armed, clientCompanyId, workspaceId }: Props) {
    const { route } = useLocation()
    if (armed.length === 0) return null

    const scoped = routes.scoped(clientCompanyId, workspaceId)
    return (
        <div class="border-b border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-800/40">
            <div class="flex items-center justify-between gap-3 px-6 pb-2 pt-3">
                <h2 class="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Armed broadcasts <span class="tabular-nums text-slate-400 dark:text-slate-500">· {armed.length}</span>
                </h2>
                <span class="hidden text-xs text-slate-400 dark:text-slate-500 sm:block">Not yet materialized · target size, final audience set at send</span>
            </div>
            <ul class="px-3 pb-3">
                {armed.map(b => {
                    const manageHref = b.CampaignId && b.WebId ? scoped.web(b.CampaignId, b.WebId)
                        : b.CampaignId ? scoped.campaign(b.CampaignId) : null
                    return (
                        <li key={b.TouchPointId}>
                            <button type="button" onClick={() => manageHref && route(manageHref)} disabled={!manageHref}
                                class="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left hover:bg-white disabled:cursor-default dark:hover:bg-slate-800">
                                <Send size={15} class="shrink-0 text-slate-400 dark:text-slate-500" />
                                <span class="min-w-0 flex-1">
                                    <span class="block truncate font-medium text-slate-900 dark:text-white">{b.Name}</span>
                                    <span class="block truncate text-xs text-slate-400 dark:text-slate-500">{[b.CampaignName, b.WebName].filter(Boolean).join(' · ') || '—'}</span>
                                </span>
                                <span class="hidden shrink-0 text-right sm:block">
                                    <span class="block text-sm tabular-nums text-slate-700 dark:text-slate-200">{b.TargetedCount != null ? `~${b.TargetedCount.toLocaleString()}` : '—'}</span>
                                    <span class="block text-xs text-slate-400 dark:text-slate-500">targeted</span>
                                </span>
                                <span class="shrink-0 text-sm tabular-nums text-slate-700 dark:text-slate-200">{b.ScheduledSendAtUtc ? relativeFromNow(b.ScheduledSendAtUtc) : '—'}</span>
                                <span class="inline-flex shrink-0 items-center rounded bg-purple-100 px-1.5 py-0.5 text-[11px] font-medium text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">Armed</span>
                            </button>
                        </li>
                    )
                })}
            </ul>
        </div>
    )
}
